/**
 * Structural suffix words that appear across many field names (`id`, `unit`, `version`, …) and instrument
 * headers. They carry almost no signal about which item a name refers to, so they are matched but not
 * counted toward the "meaningful overlap" that qualifies a match. Without this, e.g. `transfer_unit_id`
 * would map to a `TransferID` row-id column purely on the shared `id`/`transfer` tokens.
 */
export const DEFAULT_GENERIC_TOKENS: ReadonlySet<string> = new Set([
  'id',
  'ids',
  'index',
  'idx',
  'position',
  'pos',
  'amount',
  'amt',
  'unit',
  'units',
  'version',
  'ver',
  'num',
  'number',
  'type',
  'value',
  'val',
  'code',
  'name',
  'key',
  'ref',
]);

/** Default minimum number of meaningful (non-generic) shared tokens required to match two names. */
export const DEFAULT_MIN_MEANINGFUL_TOKEN_OVERLAP = 2;

/** Default shortest token eligible for prefix-abbreviation matching (so `dest`↔`destination`, but not `id`). */
export const DEFAULT_MIN_PREFIX_MATCH_LENGTH = 4;

export interface MatchByNameTokensOptions<Target, Source> {
  getTargetName: (target: Target) => string;
  getSourceName: (source: Source) => string;
  /** Pairs for which this returns false are never matched, however well their names match. */
  isCompatible?: (target: Target, source: Source) => boolean;
  genericTokens?: ReadonlySet<string>;
  minMeaningfulTokenOverlap?: number;
  minPrefixMatchLength?: number;
}

export interface NameTokenMatch<Target, Source> {
  target: Target;
  source: Source;
}

/**
 * Break a name into lowercase word tokens, splitting on camelCase boundaries, letter/digit boundaries, and
 * any separator that is not a letter, combining mark, or number in any script. `"SourcePlateBarcode"` and
 * `"source_plate_id"` both tokenize to word lists that can be compared directly, and non-Latin names such as
 * `"濃度"` or `"Löslichkeit"` stay whole words.
 */
function tokenize(value: string): Array<string> {
  return value
    .replace(/([\p{Ll}\p{N}])(\p{Lu})/gu, '$1 $2')
    .replace(/([\p{L}\p{M}])(\p{N})/gu, '$1 $2')
    .replace(/(\p{N})(\p{L})/gu, '$1 $2')
    .split(/[^\p{L}\p{M}\p{N}]+/u)
    .map((token) => token.toLowerCase())
    .filter((token) => token.length > 0);
}

/**
 * The key two names share when `matchByNameTokens` treats them as the same full name, ignoring casing and
 * separators: `"CH1(Heavy)"` and `"ch1_heavy"` both become `"ch1heavy"`.
 */
export function getNormalizedName(value: string): string {
  return tokenize(value).join('');
}

/** Two tokens match if equal, or if the shorter is a prefix of the longer (handling abbreviations like dest). */
function tokensMatch(a: string, b: string, minPrefixMatchLength: number): boolean {
  if (a === b) {
    return true;
  }
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  return shorter.length >= minPrefixMatchLength && longer.startsWith(shorter);
}

/** Count how many distinct meaningful target tokens have a matching meaningful source token. */
function countMeaningfulOverlap(
  targetMeaningfulTokens: Array<string>,
  sourceMeaningfulTokens: Array<string>,
  minPrefixMatchLength: number
): number {
  const usedSourceTokens = new Set<number>();
  let overlap = 0;
  for (const targetToken of targetMeaningfulTokens) {
    const matchIndex = sourceMeaningfulTokens.findIndex(
      (sourceToken, index) =>
        !usedSourceTokens.has(index) && tokensMatch(targetToken, sourceToken, minPrefixMatchLength)
    );
    if (matchIndex !== -1) {
      usedSourceTokens.add(matchIndex);
      overlap += 1;
    }
  }
  return overlap;
}

interface ScoredMatch<Target, Source> extends NameTokenMatch<Target, Source> {
  overlap: number;
  /**
   * Fraction of the target's and source's meaningful tokens that matched (Jaccard-style). Breaks ties between
   * targets competing for the same source: a target whose name is fully explained by the source beats one
   * that merely shares as many tokens but carries extra unmatched ones (e.g. "Destination plate index" beats
   * "Source destination plate index" for a "DestPlateIdx" column).
   */
  coverage: number;
}

/**
 * Match targets to sources by shared, meaningful name tokens.
 *
 * A target is only matched to a compatible source that shares at least `minMeaningfulTokenOverlap`
 * meaningful tokens, or whose full name matches exactly once tokenized. Matches are assigned best-first — by
 * overlap, then by coverage, then by input order — so the clearest pairings win, and each target and source
 * is used at most once. Targets with no confident match are left out rather than filled with a best-effort
 * guess.
 */
export function matchByNameTokens<Target, Source>(
  targets: ReadonlyArray<Target>,
  sources: ReadonlyArray<Source>,
  {
    getTargetName,
    getSourceName,
    isCompatible = () => true,
    genericTokens = DEFAULT_GENERIC_TOKENS,
    minMeaningfulTokenOverlap = DEFAULT_MIN_MEANINGFUL_TOKEN_OVERLAP,
    minPrefixMatchLength = DEFAULT_MIN_PREFIX_MATCH_LENGTH,
  }: MatchByNameTokensOptions<Target, Source>
): Array<NameTokenMatch<Target, Source>> {
  const meaningfulTokens = (tokens: Array<string>): Array<string> =>
    [...new Set(tokens)].filter((token) => !genericTokens.has(token));
  const sourceTokens = sources.map((source) => tokenize(getSourceName(source)));
  const sourceMeaningfulTokens = sourceTokens.map((tokens) => meaningfulTokens(tokens));
  const sourceNormalizedNames = sources.map((source) => getNormalizedName(getSourceName(source)));

  const candidates: Array<ScoredMatch<Target, Source>> = [];
  for (const target of targets) {
    const targetTokens = tokenize(getTargetName(target));
    const targetMeaningful = meaningfulTokens(targetTokens);
    const normalizedTarget = getNormalizedName(getTargetName(target));
    sources.forEach((source, sourceIndex) => {
      if (!isCompatible(target, source)) {
        return;
      }
      const sourceMeaningful = sourceMeaningfulTokens[sourceIndex];
      const overlap = countMeaningfulOverlap(targetMeaningful, sourceMeaningful, minPrefixMatchLength);
      const isExactNameMatch =
        normalizedTarget.length > 0 && normalizedTarget === sourceNormalizedNames[sourceIndex];
      if (overlap >= minMeaningfulTokenOverlap || isExactNameMatch) {
        const distinctMeaningful = new Set([...targetMeaningful, ...sourceMeaningful]).size;
        candidates.push({
          target,
          source,
          overlap: isExactNameMatch ? Number.MAX_SAFE_INTEGER : overlap,
          coverage: distinctMeaningful === 0 ? 1 : overlap / distinctMeaningful,
        });
      }
    });
  }

  candidates.sort((a, b) => b.overlap - a.overlap || b.coverage - a.coverage);
  const assignedTargets = new Set<Target>();
  const assignedSources = new Set<Source>();
  const result: Array<NameTokenMatch<Target, Source>> = [];
  for (const {target, source} of candidates) {
    if (assignedTargets.has(target) || assignedSources.has(source)) {
      continue;
    }
    assignedTargets.add(target);
    assignedSources.add(source);
    result.push({target, source});
  }
  return result;
}
